/**
 * Security Monitoring Service
 * Tracks and reports security events using Sentry and custom logging
 */

import * as Sentry from "@sentry/react";
import { logger } from "../../utils/sentry";

export enum SecurityEventType {
  LOGIN_ATTEMPT = "LOGIN_ATTEMPT",
  LOGIN_SUCCESS = "LOGIN_SUCCESS",
  LOGIN_FAILURE = "LOGIN_FAILURE",
  LOGOUT = "LOGOUT",
  PASSWORD_RESET = "PASSWORD_RESET",
  SUSPICIOUS_ACTIVITY = "SUSPICIOUS_ACTIVITY",
  RATE_LIMIT_EXCEEDED = "RATE_LIMIT_EXCEEDED",
  INVALID_TOKEN = "INVALID_TOKEN",
  UNAUTHORIZED_ACCESS = "UNAUTHORIZED_ACCESS",
  DATA_BREACH_ATTEMPT = "DATA_BREACH_ATTEMPT",
  XSS_ATTEMPT = "XSS_ATTEMPT",
  SQL_INJECTION_ATTEMPT = "SQL_INJECTION_ATTEMPT",
  CSRF_VIOLATION = "CSRF_VIOLATION",
  SESSION_HIJACK_ATTEMPT = "SESSION_HIJACK_ATTEMPT",
}

export enum SecurityEventSeverity {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical",
}

interface SecurityEvent {
  type: SecurityEventType;
  severity: SecurityEventSeverity;
  userId?: string;
  email?: string;
  ip?: string;
  userAgent?: string;
  details?: Record<string, any>;
  timestamp: Date;
}

class SecurityMonitoring {
  private eventHistory: SecurityEvent[] = [];
  private readonly maxHistorySize = 1000;
  private failedLoginAttempts: Map<string, number> = new Map();
  private readonly maxFailedAttempts = 5;
  private readonly lockoutDuration = 15 * 60 * 1000; // 15 minutes
  private lockedOutUsers: Map<string, number> = new Map();

  /**
   * Log a security event
   */
  logSecurityEvent(event: Omit<SecurityEvent, "timestamp">): void {
    const fullEvent: SecurityEvent = {
      ...event,
      timestamp: new Date(),
    };

    // Add to local history
    this.eventHistory.push(fullEvent);
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory.shift();
    }

    // Determine Sentry level based on severity
    const sentryLevel = this.getSentryLevel(event.severity);

    // Log to Sentry
    if (
      event.severity === SecurityEventSeverity.HIGH ||
      event.severity === SecurityEventSeverity.CRITICAL
    ) {
      Sentry.captureMessage(`Security Event: ${event.type}`, {
        level: sentryLevel,
        tags: {
          security_event: event.type,
          severity: event.severity,
        },
        extra: {
          userId: event.userId,
          email: event.email,
          ip: event.ip,
          userAgent: event.userAgent,
          details: event.details,
        },
      });
    }

    // Log using structured logging
    logger.warn(`Security Event: ${event.type}`, {
      type: event.type,
      severity: event.severity,
      userId: event.userId,
      email: event.email,
      details: event.details,
    });

    // Handle specific event types
    this.handleSecurityEvent(fullEvent);
  }

  /**
   * Handle specific security events
   */
  private handleSecurityEvent(event: SecurityEvent): void {
    switch (event.type) {
      case SecurityEventType.LOGIN_FAILURE:
        this.handleFailedLogin(event);
        break;
      case SecurityEventType.LOGIN_SUCCESS:
        this.handleSuccessfulLogin(event);
        break;
      case SecurityEventType.SUSPICIOUS_ACTIVITY:
      case SecurityEventType.DATA_BREACH_ATTEMPT:
      case SecurityEventType.SQL_INJECTION_ATTEMPT:
      case SecurityEventType.XSS_ATTEMPT:
        this.handleCriticalEvent(event);
        break;
    }
  }

  /**
   * Handle failed login attempts
   */
  private handleFailedLogin(event: SecurityEvent): void {
    const identifier = event.email || event.ip || "unknown";
    const attempts = (this.failedLoginAttempts.get(identifier) || 0) + 1;
    this.failedLoginAttempts.set(identifier, attempts);

    if (attempts >= this.maxFailedAttempts) {
      this.lockoutUser(identifier);
      this.logSecurityEvent({
        type: SecurityEventType.RATE_LIMIT_EXCEEDED,
        severity: SecurityEventSeverity.HIGH,
        email: event.email,
        ip: event.ip,
        details: {
          reason: "Too many failed login attempts",
          attempts,
        },
      });
    }
  }

  /**
   * Handle successful login
   */
  private handleSuccessfulLogin(event: SecurityEvent): void {
    const identifier = event.email || event.ip || "unknown";
    this.failedLoginAttempts.delete(identifier);
    this.lockedOutUsers.delete(identifier);
  }

  /**
   * Handle critical security events
   */
  private handleCriticalEvent(event: SecurityEvent): void {
    // Send immediate alert
    Sentry.captureException(
      new Error(`Critical Security Event: ${event.type}`),
      {
        tags: {
          security_critical: true,
          event_type: event.type,
        },
        extra: { ...event },
      }
    );

    // You could also trigger other alerts here (email, SMS, etc.)
    console.error("🚨 CRITICAL SECURITY EVENT:", event);
  }

  /**
   * Lock out a user after too many failed attempts
   */
  private lockoutUser(identifier: string): void {
    this.lockedOutUsers.set(identifier, Date.now() + this.lockoutDuration);
  }

  /**
   * Check if a user is locked out
   */
  isUserLockedOut(identifier: string): boolean {
    const lockoutTime = this.lockedOutUsers.get(identifier);
    if (!lockoutTime) return false;

    if (Date.now() > lockoutTime) {
      this.lockedOutUsers.delete(identifier);
      return false;
    }

    return true;
  }

  /**
   * Get remaining lockout time in seconds
   */
  getRemainingLockoutTime(identifier: string): number {
    const lockoutTime = this.lockedOutUsers.get(identifier);
    if (!lockoutTime) return 0;

    const remaining = Math.max(0, lockoutTime - Date.now());
    return Math.ceil(remaining / 1000);
  }

  /**
   * Convert severity to Sentry level
   */
  private getSentryLevel(
    severity: SecurityEventSeverity
  ): Sentry.SeverityLevel {
    switch (severity) {
      case SecurityEventSeverity.LOW:
        return "info";
      case SecurityEventSeverity.MEDIUM:
        return "warning";
      case SecurityEventSeverity.HIGH:
        return "error";
      case SecurityEventSeverity.CRITICAL:
        return "fatal";
      default:
        return "warning";
    }
  }

  /**
   * Get security event history
   */
  getEventHistory(filter?: {
    type?: SecurityEventType;
    severity?: SecurityEventSeverity;
    userId?: string;
    since?: Date;
  }): SecurityEvent[] {
    let events = [...this.eventHistory];

    if (filter) {
      if (filter.type) {
        events = events.filter((e) => e.type === filter.type);
      }
      if (filter.severity) {
        events = events.filter((e) => e.severity === filter.severity);
      }
      if (filter.userId) {
        events = events.filter((e) => e.userId === filter.userId);
      }
      if (filter.since) {
        events = events.filter((e) => e.timestamp >= filter.since!);
      }
    }

    return events;
  }

  /**
   * Get security metrics
   */
  getSecurityMetrics(): {
    totalEvents: number;
    eventsBySeverity: Record<SecurityEventSeverity, number>;
    eventsByType: Partial<Record<SecurityEventType, number>>;
    recentHighSeverityEvents: SecurityEvent[];
    currentlyLockedOutUsers: number;
  } {
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const metrics = {
      totalEvents: this.eventHistory.length,
      eventsBySeverity: {
        [SecurityEventSeverity.LOW]: 0,
        [SecurityEventSeverity.MEDIUM]: 0,
        [SecurityEventSeverity.HIGH]: 0,
        [SecurityEventSeverity.CRITICAL]: 0,
      },
      eventsByType: {} as Partial<Record<SecurityEventType, number>>,
      recentHighSeverityEvents: [] as SecurityEvent[],
      currentlyLockedOutUsers: 0,
    };

    for (const event of this.eventHistory) {
      // Count by severity
      metrics.eventsBySeverity[event.severity]++;

      // Count by type
      metrics.eventsByType[event.type] =
        (metrics.eventsByType[event.type] || 0) + 1;

      // Collect recent high severity events
      if (
        event.timestamp >= oneDayAgo &&
        (event.severity === SecurityEventSeverity.HIGH ||
          event.severity === SecurityEventSeverity.CRITICAL)
      ) {
        metrics.recentHighSeverityEvents.push(event);
      }
    }

    // Count currently locked out users
    for (const [, lockoutTime] of this.lockedOutUsers) {
      if (Date.now() < lockoutTime) {
        metrics.currentlyLockedOutUsers++;
      }
    }

    return metrics;
  }

  /**
   * Clear old events from history
   */
  clearOldEvents(daysToKeep: number = 30): void {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);

    this.eventHistory = this.eventHistory.filter(
      (event) => event.timestamp >= cutoffDate
    );
  }

  /**
   * Clear all events from history (for testing)
   */
  clearAllEvents(): void {
    this.eventHistory = [];
    this.failedLoginAttempts.clear();
    this.lockedOutUsers.clear();
  }

  /**
   * Detect suspicious patterns
   */
  detectSuspiciousPatterns(userId: string): boolean {
    const userEvents = this.getEventHistory({
      userId,
      since: new Date(Date.now() - 60 * 60 * 1000),
    });

    // Check for rapid fire events (possible automation)
    const eventTimes = userEvents.map((e) => e.timestamp.getTime());
    for (let i = 1; i < eventTimes.length; i++) {
      if (eventTimes[i] - eventTimes[i - 1] < 100) {
        // Less than 100ms between events
        return true;
      }
    }

    // Check for unusual patterns
    const failedLogins = userEvents.filter(
      (e) => e.type === SecurityEventType.LOGIN_FAILURE
    ).length;
    if (failedLogins > 3) return true;

    const suspiciousEvents = userEvents.filter(
      (e) =>
        e.type === SecurityEventType.SUSPICIOUS_ACTIVITY ||
        e.type === SecurityEventType.XSS_ATTEMPT ||
        e.type === SecurityEventType.SQL_INJECTION_ATTEMPT
    ).length;
    if (suspiciousEvents > 0) return true;

    return false;
  }
}

// Export singleton instance
export const securityMonitoring = new SecurityMonitoring();

// Export convenience functions
export function logSecurityEvent(
  event: Omit<SecurityEvent, "timestamp">
): void {
  securityMonitoring.logSecurityEvent(event);
}

export function isUserLockedOut(identifier: string): boolean {
  return securityMonitoring.isUserLockedOut(identifier);
}

export function getRemainingLockoutTime(identifier: string): number {
  return securityMonitoring.getRemainingLockoutTime(identifier);
}

export function getSecurityMetrics() {
  return securityMonitoring.getSecurityMetrics();
}
